import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveChatNotifications } from '@/contexts/LiveChatNotificationContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceMessageRecorder from '@/components/VoiceMessageRecorder';
import {
  MessageSquare, Send, Search, RefreshCw, User, Mail, Phone,
  Clock, Tag, ArrowLeft, MoreVertical, UserPlus, ArrowRightLeft,
  CheckCircle, XCircle, Globe, Monitor, Smartphone, FileText,
  StickyNote, ChevronRight, Inbox, Users, Filter, Bell, BellOff, Volume2,
  PhoneIncoming, AlertCircle
} from 'lucide-react';

// Format waiting time (e.g., "2m 15s")
const formatWaitingTime = (initiatedAt) => {
  if (!initiatedAt) return '';
  const now = new Date();
  const initiated = new Date(initiatedAt);
  const diffMs = now - initiated;
  const diffSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffSeconds / 60);
  const seconds = diffSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

// Check if waiting time exceeds threshold (2 minutes)
const isWaitingTooLong = (initiatedAt) => {
  if (!initiatedAt) return false;
  const now = new Date();
  const initiated = new Date(initiatedAt);
  const diffMs = now - initiated;
  return diffMs > 2 * 60 * 1000;
};

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatChats = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { chatId } = useParams();
  
  // Use global notification context
  const {
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
    unattendedCount,
    unattendedChats,
    enableSound,
    disableSound,
    testSound,
    handleChatAccepted,
  } = useLiveChatNotifications();
  
  // Chat list state
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Initialize viewFilter from URL parameter 'filter' (e.g., ?filter=unassigned)
  const [viewFilter, setViewFilter] = useState(searchParams.get('filter') || 'all'); // all, my, unassigned
  
  // Selected chat state
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [visitor, setVisitor] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  
  // Data
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Track waiting times - updates every second
  const [, setWaitingTimeTick] = useState(0);
  
  // Show sound prompt on first visit
  const [showSoundPrompt, setShowSoundPrompt] = useState(() => {
    return !localStorage.getItem('livechat_sound_prompt_shown');
  });
  
  // ============ WAITING TIME TICKER ============
  useEffect(() => {
    const ticker = setInterval(() => {
      setWaitingTimeTick(t => t + 1);
    }, 1000);
    return () => clearInterval(ticker);
  }, []);
  
  // ============ HANDLE SOUND PROMPT ============
  const handleEnableSound = useCallback(() => {
    enableSound();
    localStorage.setItem('livechat_sound_prompt_shown', 'true');
    setShowSoundPrompt(false);
  }, [enableSound]);
  
  const handleDismissSoundPrompt = useCallback(() => {
    localStorage.setItem('livechat_sound_prompt_shown', 'true');
    setShowSoundPrompt(false);
  }, []);
  
  // Handle sound toggle
  const handleSoundToggle = useCallback(() => {
    if (soundEnabled) {
      disableSound();
    } else {
      enableSound();
    }
  }, [soundEnabled, enableSound, disableSound]);
  
  const handleTestSound = useCallback(() => {
    testSound();
  }, [testSound]);
  
  // ============ NEW CHAT HANDLER ============
  const handleNewChat = useCallback((chat) => {
    if (!soundEnabled) return;
    
    const chatId = chat.id;
    const visitorName = chat.visitor_name || 'Visitor';
    const source = chat.source || 'website';
    const initiatedAt = chat.created_at || new Date().toISOString();
    
    // Add to unattended chats
    setUnattendedChats(prev => {
      const newMap = new Map(prev);
      newMap.set(chatId, { initiatedAt, visitorName, source });
      return newMap;
    });
    
    // Update count
    setUnattendedCount(prev => prev + 1);
    
    // Start ringing for this chat (independent, no timeout)
    startRingingForChat(chatId, VOLUME_LEVELS[soundVolume]);
    
    // Show toast notification with actions
    toast.info(
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <PhoneIncoming className="text-red-500 animate-pulse" size={18} />
          <span className="font-medium">New chat from {visitorName}</span>
        </div>
        <Badge variant="outline" className="w-fit text-xs">{source}</Badge>
        <Button 
          size="sm" 
          className="mt-1 bg-emerald-600 hover:bg-emerald-700"
          onClick={() => {
            navigate(`/marketing/live-chat/chats/${chatId}`);
            fetchChatDetails(chatId);
            handleChatAccepted(chatId);
          }}
        >
          Open Chat
        </Button>
      </div>,
      {
        id: `chat-${chatId}`,
        duration: Infinity, // Don't auto-dismiss
        dismissible: true,
      }
    );
    
    // Show browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('🔔 New Live Chat', {
        body: `${visitorName} started a conversation`,
        icon: '/favicon.ico',
        tag: `chat-${chatId}`,
        requireInteraction: true,
      });
      
      notification.onclick = () => {
        window.focus();
        navigate(`/marketing/live-chat/chats/${chatId}`);
        fetchChatDetails(chatId);
        handleChatAccepted(chatId);
        notification.close();
      };
    }
  }, [soundEnabled, soundVolume, navigate, handleChatAccepted]);
  
  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);
  
  // Check for new chats and notify
  const checkForNewChats = useCallback((newChats) => {
    if (isFirstLoadRef.current) {
      // On first load, identify any existing unattended chats (status: 'open', no assigned_to)
      const existingUnattended = newChats.filter(c => 
        c.status === 'open' && !c.assigned_to
      );
      
      if (existingUnattended.length > 0) {
        const newUnattendedMap = new Map();
        existingUnattended.forEach(chat => {
          newUnattendedMap.set(chat.id, {
            initiatedAt: chat.created_at,
            visitorName: chat.visitor_name || 'Visitor',
            source: chat.source || 'website'
          });
          // Start ringing for existing unattended chats
          if (soundEnabled) {
            startRingingForChat(chat.id, VOLUME_LEVELS[soundVolume]);
          }
        });
        setUnattendedChats(newUnattendedMap);
        setUnattendedCount(existingUnattended.length);
      }
      
      previousChatIdsRef.current = new Set(newChats.map(c => c.id));
      isFirstLoadRef.current = false;
      requestNotificationPermission();
      return;
    }
    
    const currentIds = new Set(newChats.map(c => c.id));
    
    // Find truly new chats
    const newChatIds = newChats
      .filter(chat => !previousChatIdsRef.current.has(chat.id))
      .map(c => c.id);
    
    // Handle new chats
    newChatIds.forEach(newChatId => {
      const chat = newChats.find(c => c.id === newChatId);
      if (chat && chat.status === 'open' && !chat.assigned_to) {
        handleNewChat(chat);
      }
    });
    
    // Check for chats that have been claimed (status changed or assigned)
    unattendedChats.forEach((_, unattendedChatId) => {
      const chat = newChats.find(c => c.id === unattendedChatId);
      if (chat && (chat.status !== 'open' || chat.assigned_to)) {
        // Chat has been claimed/assigned
        handleChatAccepted(unattendedChatId);
      }
    });
    
    previousChatIdsRef.current = currentIds;
  }, [soundEnabled, soundVolume, handleNewChat, handleChatAccepted, unattendedChats, requestNotificationPermission]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup is handled by the LiveChatNotificationContext
    };
  }, []);

  useEffect(() => {
    fetchChats();
    fetchAgents();
    fetchTeams();
    fetchCannedResponses();
    fetchTags();
  }, [token]);
  
  // Separate effect for polling - depends on selectedChat
  useEffect(() => {
    // Poll for new chats list every 5 seconds
    const chatsInterval = setInterval(fetchChats, 5000);
    
    return () => clearInterval(chatsInterval);
  }, [token, statusFilter, viewFilter]);
  
  // Separate effect for message polling when chat is selected
  useEffect(() => {
    if (!selectedChat?.id) return;
    
    // Poll for new messages every 3 seconds (faster for active chat)
    const messagesInterval = setInterval(() => {
      fetchMessages(selectedChat.id);
    }, 3000);
    
    return () => clearInterval(messagesInterval);
  }, [selectedChat?.id, token]);

  useEffect(() => {
    if (chatId) {
      fetchChatDetails(chatId);
    }
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const params = new URLSearchParams({ token, limit: '50' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (viewFilter === 'my') params.append('my_chats', 'true');
      if (viewFilter === 'unassigned') params.append('unassigned', 'true');
      
      const response = await axios.get(`${API}/api/live-chat/chats?${params}`);
      if (response.data.success) {
        const newChats = response.data.chats;
        checkForNewChats(newChats);
        setChats(newChats);
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const fetchChatDetails = async (id) => {
    setLoadingChat(true);
    try {
      const response = await axios.get(`${API}/api/live-chat/chats/${id}?token=${token}`);
      if (response.data.success) {
        setSelectedChat(response.data.chat);
        setMessages(response.data.messages);
        setVisitor(response.data.visitor);
      }
    } catch (error) {
      toast.error('Failed to load chat');
      console.error(error);
    } finally {
      setLoadingChat(false);
    }
  };

  const fetchMessages = async (id) => {
    try {
      const response = await axios.get(`${API}/api/live-chat/chats/${id}/messages?token=${token}`);
      if (response.data.success) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/api/organization/users?token=${token}`);
      setAgents(response.data?.users || []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/api/teams?token=${token}`);
      setTeams(response.data?.teams || response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchCannedResponses = async () => {
    try {
      const response = await axios.get(`${API}/api/live-chat/canned-responses?token=${token}`);
      if (response.data.success) {
        setCannedResponses(response.data.responses);
      }
    } catch (error) {
      console.error('Failed to fetch canned responses:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/api/live-chat/tags?token=${token}`);
      if (response.data.success) {
        setTags(response.data.tags);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/api/live-chat/chats/${selectedChat.id}/messages?token=${token}`,
        { message: newMessage }
      );
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage('');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const assignChat = async (agentId) => {
    try {
      const response = await axios.post(
        `${API}/api/live-chat/chats/${selectedChat.id}/assign?token=${token}`,
        { agent_id: agentId }
      );
      
      if (response.data.success) {
        toast.success('Chat assigned');
        setShowAssignDialog(false);
        fetchChatDetails(selectedChat.id);
        fetchChats();
      }
    } catch (error) {
      toast.error('Failed to assign chat');
    }
  };

  const updateStatus = async (status) => {
    try {
      const response = await axios.put(
        `${API}/api/live-chat/chats/${selectedChat.id}/status?token=${token}`,
        { status }
      );
      
      if (response.data.success) {
        toast.success(`Chat marked as ${status}`);
        fetchChatDetails(selectedChat.id);
        fetchChats();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const applyCannedResponse = (response) => {
    setNewMessage(response.message);
  };

  const statusColors = {
    open: 'bg-blue-50 text-blue-700 border-blue-200',
    assigned: 'bg-violet-50 text-violet-700 border-violet-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    closed: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  // Format time in UAE timezone (Asia/Dubai - UTC+4)
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AE', { 
      timeZone: 'Asia/Dubai',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Get today and yesterday in UAE timezone
    const uaeNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    const today = new Date(uaeNow);
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Convert the input date to UAE timezone for comparison
    const uaeDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    uaeDate.setHours(0, 0, 0, 0);
    
    if (uaeDate.getTime() === today.getTime()) return 'Today';
    if (uaeDate.getTime() === yesterday.getTime()) return 'Yesterday';
    return date.toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai' });
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="live-chat-chats">
      {/* Sound Enable Prompt - First time only */}
      {showSoundPrompt && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="text-amber-600" size={20} />
            <div>
              <p className="font-medium text-amber-800">Enable Sound Alerts</p>
              <p className="text-sm text-amber-600">Get notified when new chats arrive</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleDismissSoundPrompt}>
              Maybe Later
            </Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={handleEnableSound}>
              Enable Sound
            </Button>
          </div>
        </div>
      )}
      
      {/* Incoming Chat Banner - Shows when there are unattended chats */}
      {unattendedCount > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <PhoneIncoming className="text-red-600" size={24} />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
              </div>
              <div>
                <p className="font-semibold text-red-800">
                  {unattendedCount} Incoming {unattendedCount === 1 ? 'Chat' : 'Chats'}
                </p>
                <p className="text-sm text-red-600">Waiting for response</p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                // Open the first unattended chat
                const firstUnattended = Array.from(unattendedChats.keys())[0];
                if (firstUnattended) {
                  navigate(`/marketing/live-chat/chats/${firstUnattended}`);
                  fetchChatDetails(firstUnattended);
                  handleChatAccepted(firstUnattended);
                }
              }}
            >
              Answer Now
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">Chat Inbox</h1>
              {/* Unattended Count Badge */}
              {unattendedCount > 0 ? (
                <Badge className="bg-red-500 text-white animate-pulse">
                  {unattendedCount} Unattended
                </Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700">
                  All chats attended
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">Manage live chat conversations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Volume Control */}
          {soundEnabled && (
            <Select value={soundVolume} onValueChange={setSoundVolume}>
              <SelectTrigger className="w-24 h-8">
                <Volume2 size={14} className="mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button
            variant={soundEnabled ? "default" : "outline"}
            size="sm"
            onClick={handleSoundToggle}
            className={`gap-2 ${soundEnabled ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-slate-500'}`}
            title={soundEnabled ? 'Sound notifications enabled - click to disable' : 'Sound notifications disabled - click to enable'}
            data-testid="toggle-sound-btn"
          >
            {soundEnabled ? <Bell size={14} /> : <BellOff size={14} />}
            {soundEnabled ? 'Sound On' : 'Sound Off'}
          </Button>
          {soundEnabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTestSound}
              className="gap-1 text-slate-500 hover:text-slate-700"
              title="Test notification sound"
              data-testid="test-sound-btn"
            >
              <Volume2 size={14} />
              Test
            </Button>
          )}
          <Button onClick={fetchChats} variant="outline" size="sm" className="gap-2">
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Chat List Panel */}
        <Card className="w-[360px] flex flex-col border-slate-200">
          {/* Filters */}
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="flex gap-2">
              <Select value={viewFilter} onValueChange={setViewFilter}>
                <SelectTrigger className="flex-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Chats</SelectItem>
                  <SelectItem value="my">My Chats</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Chat List */}
          <ScrollArea className="flex-1">
            {loadingChats ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Inbox size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No chats found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {chats.map(chat => {
                  const isUnattended = unattendedChats.has(chat.id);
                  const unattendedInfo = unattendedChats.get(chat.id);
                  const waitingTime = isUnattended ? formatWaitingTime(unattendedInfo?.initiatedAt) : '';
                  const waitingTooLong = isUnattended ? isWaitingTooLong(unattendedInfo?.initiatedAt) : false;
                  
                  return (
                    <div
                      key={chat.id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors relative ${
                        selectedChat?.id === chat.id ? 'bg-slate-100' : ''
                      } ${isUnattended ? 'border-l-4 border-l-red-500 bg-red-50/50' : ''}`}
                      onClick={() => {
                        navigate(`/marketing/live-chat/chats/${chat.id}`);
                        fetchChatDetails(chat.id);
                        // Mark as accepted when clicked
                        if (isUnattended) {
                          handleChatAccepted(chat.id);
                        }
                      }}
                      data-testid={`chat-item-${chat.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarFallback className={`${isUnattended ? 'bg-red-200 text-red-700' : 'bg-slate-200 text-slate-600'}`}>
                            {(chat.visitor_name || 'V')[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className="font-medium text-slate-900 truncate">
                              {chat.visitor_name || 'Visitor'}
                            </p>
                            <span className="text-xs text-slate-400">
                              {formatTime(chat.updated_at)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 truncate">
                            {chat.last_message?.message || 'No messages'}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-xs ${statusColors[chat.status]}`}>
                              {chat.status}
                            </Badge>
                            {isUnattended && (
                              <>
                                <Badge className="bg-red-500 text-white text-xs animate-pulse">
                                  Unattended
                                </Badge>
                                <span className={`text-xs flex items-center gap-1 ${waitingTooLong ? 'text-red-600 font-medium' : 'text-amber-600'}`}>
                                  <Clock size={10} />
                                  Waiting {waitingTime}
                                </span>
                              </>
                            )}
                            {chat.unread_count > 0 && !isUnattended && (
                              <Badge className="bg-blue-500 text-white text-xs h-5 min-w-5 flex items-center justify-center">
                                {chat.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Chat Detail Panel */}
        {selectedChat ? (
          <Card className="flex-1 flex flex-col border-slate-200">
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-slate-200 text-slate-600">
                    {(selectedChat.visitor_name || 'V')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">
                      {selectedChat.visitor_name || 'Visitor'}
                    </p>
                    <Badge variant="outline" className={`text-xs ${statusColors[selectedChat.status]}`}>
                      {selectedChat.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {selectedChat.visitor_email && (
                      <span className="flex items-center gap-1">
                        <Mail size={10} /> {selectedChat.visitor_email}
                      </span>
                    )}
                    {visitor?.browser && (
                      <span className="flex items-center gap-1">
                        <Monitor size={10} /> {visitor.browser}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAssignDialog(true)}
                >
                  <UserPlus size={14} className="mr-1" /> Assign
                </Button>
                {selectedChat.status !== 'resolved' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => updateStatus('resolved')}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    <CheckCircle size={14} className="mr-1" /> Resolve
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowConvertDialog(true)}
                >
                  <User size={14} className="mr-1" /> Convert to Lead
                </Button>
              </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {loadingChat ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => {
                    const isAgent = msg.sender_type === 'agent';
                    const isSystem = msg.sender_type === 'system';
                    const showDate = idx === 0 || 
                      formatDate(msg.created_at) !== formatDate(messages[idx - 1]?.created_at);
                    
                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        )}
                        {isSystem ? (
                          <div className="text-center">
                            <span className="text-xs text-slate-500 italic">{msg.message}</span>
                          </div>
                        ) : (
                          <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] ${
                              isAgent 
                                ? 'bg-slate-900 text-white rounded-2xl rounded-br-md' 
                                : 'bg-slate-100 text-slate-900 rounded-2xl rounded-bl-md'
                            } px-4 py-2.5`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                              <p className={`text-xs mt-1 ${
                                isAgent ? 'text-slate-400' : 'text-slate-500'
                              }`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Canned Responses */}
            {cannedResponses.length > 0 && (
              <div className="px-4 py-2 border-t border-slate-100">
                <ScrollArea className="w-full whitespace-nowrap">
                  <div className="flex gap-2">
                    {cannedResponses.slice(0, 5).map(response => (
                      <Button
                        key={response.id}
                        variant="outline"
                        size="sm"
                        className="text-xs flex-shrink-0"
                        onClick={() => applyCannedResponse(response)}
                      >
                        {response.shortcut || response.title}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t border-slate-100">
              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="min-h-[44px] max-h-[120px] resize-none"
                  rows={1}
                  data-testid="chat-message-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="h-auto"
                  data-testid="send-message-btn"
                >
                  <Send size={18} />
                </Button>
                <VoiceMessageRecorder
                  chatType="live_chat"
                  contactId={selectedChat?.id || null}
                  compact
                />
              </div>
            </div>
          </Card>
        ) : (
          <Card className="flex-1 flex items-center justify-center border-slate-200">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Select a chat to view conversation</p>
            </div>
          </Card>
        )}

        {/* Visitor Info Panel */}
        {selectedChat && visitor && (
          <Card className="w-[280px] p-4 border-slate-200">
            <h3 className="font-medium text-slate-900 mb-4">Visitor Info</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Name</p>
                <p className="text-sm font-medium">{visitor.visitor_name || 'Anonymous'}</p>
              </div>
              
              {visitor.visitor_email && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="text-sm">{visitor.visitor_email}</p>
                </div>
              )}
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Current Page</p>
                <p className="text-sm truncate" title={visitor.current_page}>
                  {visitor.current_page?.replace(/^https?:\/\/[^/]+/, '') || '/'}
                </p>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Entry Page</p>
                <p className="text-sm truncate" title={visitor.entry_page}>
                  {visitor.entry_page?.replace(/^https?:\/\/[^/]+/, '') || '/'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Pages Viewed</p>
                  <p className="text-sm font-medium">{visitor.page_views || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Visit Count</p>
                  <p className="text-sm font-medium">{visitor.visit_count || 1}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Device</p>
                  <p className="text-sm">{visitor.device_type || 'Desktop'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Browser</p>
                  <p className="text-sm">{visitor.browser || 'Unknown'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">OS</p>
                <p className="text-sm">{visitor.os || 'Unknown'}</p>
              </div>
              
              {selectedChat.linked_contact_id && (
                <div className="pt-3 border-t border-slate-100">
                  <Badge className="bg-emerald-50 text-emerald-700">
                    <User size={12} className="mr-1" /> Linked to Lead
                  </Badge>
                </div>
              )}
            </div>
            
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => navigate(`/marketing/live-chat/visitors/${visitor.visitor_id}`)}
            >
              View Full Profile
            </Button>
          </Card>
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Chat</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">Select an agent to assign this chat to:</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {agents.map(agent => (
                <div
                  key={agent.id}
                  className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg cursor-pointer"
                  onClick={() => assignChat(agent.id)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-slate-200 text-xs">
                        {(agent.full_name || agent.email || 'A')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{agent.full_name || agent.email}</p>
                      <p className="text-xs text-slate-500">{agent.role}</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Lead Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Lead</DialogTitle>
          </DialogHeader>
          <ConvertToLeadForm 
            chat={selectedChat}
            visitor={visitor}
            token={token}
            onSuccess={() => {
              setShowConvertDialog(false);
              fetchChatDetails(selectedChat.id);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Convert to Lead Form Component
const ConvertToLeadForm = ({ chat, visitor, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: visitor?.visitor_name || chat?.visitor_name || '',
    email: visitor?.visitor_email || chat?.visitor_email || '',
    phone: visitor?.visitor_phone || chat?.visitor_phone || '',
    company: '',
    notes: '',
    include_transcript: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/live-chat/chats/${chat.id}/convert-to-lead?token=${token}`,
        formData
      );
      
      if (response.data.success) {
        toast.success('Lead created successfully');
        onSuccess();
      } else {
        toast.error(response.data.message || 'Failed to create lead');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Lead name"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Email</label>
          <Input
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@example.com"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Phone</label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1234567890"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Company</label>
          <Input
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Company name"
          />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium">Notes</label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="include_transcript"
          checked={formData.include_transcript}
          onChange={(e) => setFormData({ ...formData, include_transcript: e.target.checked })}
        />
        <label htmlFor="include_transcript" className="text-sm">Include chat transcript in lead notes</label>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onSuccess()}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating...' : 'Create Lead'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default LiveChatChats;
